import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import Papa from 'papaparse';
import { Plus, Trash2, Upload, Download, AlertCircle } from 'lucide-react';

export function TeamsStep({ eventData, setEventData, errors, newTeamName, setNewTeamName, isAuthenticated, isPro }) {
  const addTeam = () => {
    if (newTeamName.trim()) {
      if (isAuthenticated && !isPro && eventData.teams.length >= 8) {
        toast.error('Free tier limited to 8 teams. Upgrade to Pro for unlimited teams.');
        return;
      }
      setEventData(prev => ({
        ...prev,
        teams: [...prev.teams, { id: `team_${Date.now()}`, name: newTeamName.trim(), seed: prev.teams.length + 1 }]
      }));
      setNewTeamName('');
    }
  };

  const removeTeam = (teamId) => {
    setEventData(prev => ({
      ...prev,
      teams: prev.teams.filter(t => t.id !== teamId).map((t, i) => ({ ...t, seed: i + 1 }))
    }));
  };

  const handleCSVImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        complete: (results) => {
          const teams = results.data
            .filter(row => row[0] && row[0].trim())
            .map((row, i) => ({ id: `team_${Date.now()}_${i}`, name: row[0].trim(), seed: i + 1 }));
          if (teams.length > 0) {
            setEventData(prev => ({ ...prev, teams: [...prev.teams, ...teams] }));
            toast.success(`Imported ${teams.length} teams`);
          }
        },
        error: () => { toast.error('Failed to parse CSV file'); }
      });
    }
    event.target.value = '';
  };

  const downloadCSVTemplate = () => {
    const csvContent = "Team Name\nTeam 1\nTeam 2\nTeam 3\nTeam 4";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'teams_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    toast.success('Template downloaded');
  };

  return (
    <div className="space-y-6 wizard-step">
      <div className="flex flex-col sm:flex-row gap-3">
        <Input data-testid="new-team-input" value={newTeamName}
          onChange={(e) => setNewTeamName(e.target.value)}
          placeholder="Enter team name" className="h-12 flex-1"
          onKeyPress={(e) => e.key === 'Enter' && addTeam()} />
        <Button data-testid="add-team-btn" onClick={addTeam} className="bg-[#00247D] hover:bg-[#001A5C] h-12">
          <Plus className="w-4 h-4 mr-2" />Add Team
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="h-px bg-slate-200 flex-1"></div>
        <span className="text-sm text-slate-500">or</span>
        <div className="h-px bg-slate-200 flex-1"></div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <input type="file" accept=".csv" onChange={handleCSVImport} className="hidden" id="csv-upload" />
          <label htmlFor="csv-upload" className="flex-1">
            <Button variant="outline" className="w-full h-12 cursor-pointer" asChild>
              <span><Upload className="w-4 h-4 mr-2" />Import from CSV File</span>
            </Button>
          </label>
          <Button variant="outline" data-testid="download-csv-template-btn" onClick={downloadCSVTemplate} className="h-12">
            <Download className="w-4 h-4 mr-2" />Download Template
          </Button>
        </div>
        <details className="group">
          <summary className="text-xs text-[#00247D] cursor-pointer font-medium hover:underline" data-testid="bulk-paste-toggle">
            Or paste team names directly (one per line)
          </summary>
          <div className="mt-2 space-y-2">
            <textarea
              className="w-full h-24 p-3 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#00247D]/20 focus:border-[#00247D]"
              placeholder={"Team 1\nTeam 2\nTeam 3"}
              data-testid="bulk-paste-textarea"
              id="bulk-paste-area"
            />
            <Button variant="outline" size="sm" data-testid="bulk-paste-btn" onClick={() => {
              const area = document.getElementById('bulk-paste-area');
              const text = area?.value || '';
              const names = text.split('\n').map(n => n.trim()).filter(Boolean);
              if (names.length === 0) { toast.error('No team names entered'); return; }
              const existing = new Set(eventData.teams.map(t => t.name.toLowerCase()));
              const newTeams = names.filter(n => !existing.has(n.toLowerCase()))
                .map((n, i) => ({ id: `team_${Date.now()}_${i}`, name: n, seed: eventData.teams.length + i + 1 }));
              if (newTeams.length === 0) { toast.info('All teams already exist'); return; }
              if (isAuthenticated && !isPro && (eventData.teams.length + newTeams.length) > 8) {
                toast.error('Free tier limited to 8 teams. Upgrade to Pro.');
                return;
              }
              setEventData(prev => ({ ...prev, teams: [...prev.teams, ...newTeams] }));
              area.value = '';
              toast.success(`Added ${newTeams.length} team(s)`);
            }}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add All
            </Button>
          </div>
        </details>
        <p className="text-xs text-slate-500">CSV should have team names in the first column. Download the template for the correct format.</p>
      </div>

      {errors.teams && (
        <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.teams}</p>
      )}

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {eventData.teams.map((team, index) => (
          <div key={team.id} data-testid={`team-item-${index}`}
            className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg animate-slide-in-right"
            style={{ animationDelay: `${index * 0.05}s` }}>
            <span className="w-8 h-8 rounded-full bg-[#00247D] text-white flex items-center justify-center text-sm font-bold">{index + 1}</span>
            <span className="flex-1 font-medium text-slate-700">{team.name}</span>
            <Button variant="ghost" size="icon" data-testid={`remove-team-${index}`}
              onClick={() => removeTeam(team.id)} className="text-slate-400 hover:text-red-500 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      {eventData.teams.length > 0 && (
        <p className="text-sm text-slate-600">Total: <span className="font-semibold text-[#00247D]">{eventData.teams.length} teams</span></p>
      )}
    </div>
  );
}
